import { ERROR_TEXT } from '@/theme/themes/colors'
import { queryKeys, WHOLE_LIST_PARAMS } from '@/constant/common'
import { CONFIG_SETUP_ROUTES, OPERATIONS_ROUTES } from '@/constant/route'
import { comonDataApi, setupApi } from '@/api/config-setup'
import { useAuthContext } from '@/features/auth/context'
import useSearchPlant from '@/features/config/hooks/plants/useSearchPlant'
import useSearchSoil from '@/features/config/hooks/soils/useSearchSoil'
import useSearchEmitter from '@/features/config/hooks/emitter/useSearchEmitter'
import useSearchArea from '@/features/config/hooks/areasandzones/useSearchArea'
import useSearchStation from '@/features/config/hooks/station/useSearchStation'
import useSearchEtReference from '@/features/config/hooks/etReference/useSearchEtReference'
import useSearchHydraulic from '@/features/config/hooks/hydraulic/useSearchHydraulic'
import { unpinnedStationCount } from './unpinnedStationCount'
import {
  areasWithoutSubArea,
  countAreas,
  countSubAreas,
  stationStepReadiness,
  type StationPrerequisite,
} from './stationStepReadiness'
import { ModalUI, SidebarUI } from '@/features/config/ConfigContainer'
import { ConfigUIProvider } from '@/features/config/context'
import SetUpLayout from '@/features/design-system/components/common/SetUpLayout'
import useSearchInterfaceId from '@features/admin/hooks/interface/useSearchInterfaceId'
import { useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import type { Theme } from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useAppDispatch } from '@store/stores'
import { updateInterfaceStatus } from '@/store/slices/interfaceByOrganization/interfaceByOrganizationSlice'
import { getApiErrorMessage } from '@/helpers/notify'
import useTranslation from '@/hooks/useTranslation'
import { useNavigate, useParams } from 'react-router-dom'
import { GUIDED_STEPS } from './guidedSteps'
import {
  GuidedEditProvider,
  runGuidedSaves,
  GuidedSaveIncompleteError,
  GuidedSaveTimeoutError,
  useGuidedSavesRef,
  useGuidedIncompleteRef,
  type GuidedAutosaveState,
} from '@/features/config/contexts/guided-edit-context'
import useSearchAllDecoders, {
  isStationDecoder,
} from '@/features/config/hooks/controller/useSearchAllDecoders'
import GuidedSettingsMenu from './GuidedSettingsMenu'
import GuidedStepRail from './GuidedStepRail'
import {
  MUST_FILL_STEP_KEYS,
  canMoveTo,
  countMainlines,
  countWithoutName,
  countWithoutPurpose,
  firstBlockingStep,
  railStepStatus,
  shouldPollCount,
  type PolledCount,
  stepComplete,
  stepGap,
  type RailStepStatus,
  type StepGap,
} from './guidedStepGaps'
import { IoTypeMask } from '@/types/controller'
import {
  beginGuidedSetupProgress,
  clearGuidedSetupStorage,
  completeGuidedSetupProgress,
  loadGuidedSetupProgress,
  type GuidedSetupProgress,
  updateGuidedSetupProgress,
} from './guided-setup-progress'
import useGuidedSaveThen from './useGuidedSaveThen'
import { useRetryFailedQuery, useWarnBeforeUnload } from './useGuidedPageGuards'

/**
 * SCRUM-1032 / SCRUM-1033 — Guided Common Data setup shell.
 *
 * The in-app alternative to the Excel "Import Common Data" step: a per-dataset stepper the user walks
 * through, entering each common-data set directly. Reached from the Import step's route choice.
 *
 * Interface scoping (SCRUM-976): every step is driven by the URL `:id`. This page IS rendered inside
 * the matched `setup_second/guided/:id` route, so `useParams()` sees the id (unlike SetUpContainer).
 * The id is passed down to each step; steps must never read the globally-selected interface.
 */
const BLOCKED_REASON_SX = {
  color: (theme: Theme) =>
    ERROR_TEXT[theme.palette.mode],
  fontSize: '12px',
}

const BRAND = '#004744'
const BRAND_LIGHT = '#0A6B62'
const RESET_STEP = {
  key: 'resetCommonData',
  labelKey: 'guidedSetupResetCommonData',
}

/**
 * The reset is three server passes (delete-by-type 1, 2 — paged — and 3), and the
 * middle one can run for a while on a full controller. The dialog reports which
 * pass is running so a long wait reads as progress rather than a frozen modal.
 */
const RESET_PHASE_KEYS = [
  'guidedSetupResetPhaseSettings',
  'guidedSetupResetPhaseStations',
  'guidedSetupResetPhaseRemaining',
]

/** A paging loop this long is the server repeating itself, not a big site. */
const RESET_MAX_PAGES = 500

/**
 * Says which pass failed. The API's own message ("Http request failed") names
 * neither the step nor the controller, so on its own it left nothing to act on.
 */
const resetFailure = (
  t: (key: string, options?: Record<string, any>) => string,
  phase: number,
  payload: any,
) =>
  payload?.message
    ? t('guidedSetupResetFailedDetail', {
        phase: t(RESET_PHASE_KEYS[phase]),
        message: payload.message,
      })
    : t('guidedSetupResetFailed', { phase: t(RESET_PHASE_KEYS[phase]) })

/**
 * Reset progress: one row per server pass, each row either done, running or
 * pending. The running row keeps a spinner and a sweeping bar so a slow pass
 * still looks alive — the API gives no percentage to show.
 */
const ResetProgressPanel = memo(({ phase }: { phase: number }) => {
  const { t } = useTranslation()

  return (
    <Stack sx={{ px: '30px', pb: '30px', gap: '18px' }}>
      <Typography sx={{ color: 'grey.800', fontSize: '15px', lineHeight: 1.45 }}>
        {t('guidedSetupResetProgressNote')}
      </Typography>

      <Stack sx={{ gap: '10px' }}>
        {RESET_PHASE_KEYS.map((labelKey, index) => {
          const isDone = index < phase
          const isActive = index === phase
          return (
            <Stack
              key={labelKey}
              direction="row"
              sx={{
                alignItems: 'center',
                gap: '12px',
                px: '14px',
                py: '10px',
                borderRadius: '10px',
                border: '1px solid',
                borderColor: isActive ? BRAND_LIGHT : 'border.main',
                backgroundColor: (theme) =>
                  isActive
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(10,107,98,0.18)'
                      : 'rgba(0,71,68,0.06)'
                    : 'transparent',
                opacity: isDone || isActive ? 1 : 0.55,
                transition: 'border-color .25s ease, background-color .25s ease',
              }}
            >
              <Stack
                sx={{
                  width: 22,
                  height: 22,
                  flexShrink: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: isDone ? BRAND : 'transparent',
                  border: isDone ? 'none' : '1px solid',
                  borderColor: 'border.main',
                }}
              >
                {isDone ? (
                  <CheckRoundedIcon sx={{ fontSize: 14, color: '#fff' }} />
                ) : isActive ? (
                  <CircularProgress size={14} sx={{ color: BRAND_LIGHT }} />
                ) : (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: 'grey.600',
                    }}
                  />
                )}
              </Stack>
              <Typography
                sx={{
                  color: isDone || isActive ? 'text.main' : 'grey.600',
                  fontSize: '15px',
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {t(labelKey)}
              </Typography>
            </Stack>
          )
        })}
      </Stack>

      <Box
        sx={{
          position: 'relative',
          height: 4,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'border.main',
          '@keyframes guidedResetSweep': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(320%)' },
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            width: '32%',
            borderRadius: 2,
            backgroundColor: BRAND_LIGHT,
            animation: 'guidedResetSweep 1.2s ease-in-out infinite',
          },
        }}
      />
    </Stack>
  )
})
ResetProgressPanel.displayName = 'ResetProgressPanel'

const footerButtonSx = {
  height: 40,
  minWidth: 116,
  px: '18px',
  borderRadius: '10px',
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none' as const,
}

const backButtonSx = {
  ...footerButtonSx,
  color: 'text.main',
  border: '1px solid',
  borderColor: 'border.main',
  backgroundColor: 'transparent',
  '&:hover': {
    borderColor: 'primary.main',
    color: 'primary.main',
    backgroundColor: (theme: any) =>
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(0,71,68,0.05)',
  },
}

const nextButtonSx = {
  ...footerButtonSx,
  color: '#fff',
  background: `linear-gradient(120deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
  boxShadow: '0 6px 16px rgba(0,71,68,0.22)',
  transition: 'box-shadow 200ms ease, transform 200ms ease',
  '&:hover': {
    background: `linear-gradient(120deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
    boxShadow: '0 10px 22px rgba(0,71,68,0.30)',
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.75)' },
}

const disabledNextButtonSx = {
  ...footerButtonSx,
  color: 'grey.800',
  backgroundColor: 'grey.300',
  boxShadow: 'none',
  '&.Mui-disabled': {
    color: 'grey.800',
    backgroundColor: 'grey.300',
  },
}

/** Prerequisite identifier → the key that words it. */
const PREREQUISITE_KEYS: Record<StationPrerequisite, string> = {
  plant: 'guidedSetupPrerequisitePlant',
  soil: 'guidedSetupPrerequisiteSoil',
  emitter: 'guidedSetupPrerequisiteEmitter',
  area: 'guidedSetupPrerequisiteArea',
  subArea: 'guidedSetupPrerequisiteSubArea',
}

/** The steps whose grids offer the expanded view. */
const FOCUS_STEP_KEYS: ReadonlySet<string> = new Set([
  'decodersOutput',
  'decodersInput',
  'decodersSdi12',
  'station',
])

/** How often a count the open step is held on is re-read. */
const COUNT_POLL_MS = 4000

const SetUpGuidedWizard = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const interfaceId = Number(id)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const { data: selectedInterface } = useSearchInterfaceId(id ?? '')

  const steps = GUIDED_STEPS

  // SCRUM-1032 / SCRUM-1040: pull the whole output-decoder list as soon as the
  // wizard opens. The Station step builds its rows from the decoders marked as
  // stations — after the guided reset there is no station data to build them
  // from — and that needs every decoder, not the blocks the decoder grid
  // happens to have paged in. Warmed here so the step opens on data.
  // Also what the Output Decoders step is judged on once the user has left it
  // (QC round 2, item 2): the grid refetches this same list after each save.
  const outputDecodersQuery = useSearchAllDecoders(interfaceId, undefined, {
    enabled: Number.isFinite(interfaceId),
  })
  const { data: outputDecoders } = outputDecodersQuery
  // SCRUM-1032: a station needs a plant, a soil, an emitter and a sub-area
  // before it can be written. Opening the step without them presents a thousand
  // rows of empty dropdowns, so it is held until the earlier steps have
  // produced at least one of each. `PageSize: 0` fetches the whole list; the
  // counts are its length, so every re-read costs a full list.
  // Same params as the quick-add chips' list: one cache entry, so their
  // re-read after a write updates the gate at once.
  const countQuery = WHOLE_LIST_PARAMS
  // The counts were re-read on step change alone, which left the gate one step
  // behind the work: the user adds the area the message asked for, stays on the
  // step, and Next keeps refusing on a count taken before the row existed. The
  // grids write through their own APIs and invalidate nothing, so there is no
  // event to listen for — the count the open step is held on is re-read on a
  // timer (see `shouldPollCount`), and the polling stops the moment it opens.
  const stationReadyRef = useRef(false)
  /** The counts worth re-reading right now; set once the gate is known. */
  const polledCountsRef = useRef<ReadonlySet<PolledCount>>(new Set())
  const pollOptions = (count: PolledCount) =>
    ({
      enabled: Number.isFinite(interfaceId),
      refetchInterval: () =>
        polledCountsRef.current.has(count) ? COUNT_POLL_MS : false,
    }) as any
  const plantQuery = useSearchPlant(countQuery, interfaceId, pollOptions('plants'))
  const soilQuery = useSearchSoil(countQuery, interfaceId, pollOptions('soils'))
  const emitterQuery = useSearchEmitter(
    countQuery,
    interfaceId,
    pollOptions('emitters'),
  )
  const areaQuery = useSearchArea(countQuery, interfaceId, pollOptions('areas'))
  // QC round 2, item 8 — the Hydraulic step holds Next until it has a mainline.
  const hydraulicQuery = useSearchHydraulic(
    countQuery,
    interfaceId,
    pollOptions('mainlines'),
  )
  // Not a gate (Jonathan, 22 Sep): re-read on step change only.
  const listEnabled = { enabled: Number.isFinite(interfaceId) }
  const etReferenceQuery = useSearchEtReference(
    countQuery,
    interfaceId,
    listEnabled as any,
  )
  const { data: plantData, refetch: refetchPlants } = plantQuery
  const { data: soilData, refetch: refetchSoils } = soilQuery
  const { data: emitterData, refetch: refetchEmitters } = emitterQuery
  const { data: areaData, refetch: refetchAreas } = areaQuery
  const { data: hydraulicData, refetch: refetchHydraulic } = hydraulicQuery
  const { data: etReferenceData, refetch: refetchEtReferences } =
    etReferenceQuery
  // QC round 2, item 4 — what the rail marks the remaining steps on. Input and
  // SDI-12 share the grid's own cache key, so the grid's refetches reach here.
  const { data: inputDecoders } = useSearchAllDecoders(
    interfaceId,
    IoTypeMask.Input,
    listEnabled,
  )
  const { data: sdi12Decoders } = useSearchAllDecoders(
    interfaceId,
    IoTypeMask.Sdi12,
    listEnabled,
  )
  // One row is enough: the response carries the unpinned count for them all.
  // Also the station total the Station gate falls back on (QC round 7 review);
  // re-read on step change, which is when that fallback is consulted.
  const pinQuery = useSearchStation(
    { PageSize: 1, PageIndex: 1 },
    interfaceId,
    listEnabled as any,
  )
  const { data: pinData, refetch: refetchPins } = pinQuery
  // An unread count holds its gate ("checking"); with `retry: 0` a failed one
  // would hold it for good, so failures are asked again.
  useRetryFailedQuery(plantQuery)
  useRetryFailedQuery(soilQuery)
  useRetryFailedQuery(emitterQuery)
  useRetryFailedQuery(areaQuery)
  useRetryFailedQuery(hydraulicQuery)
  useRetryFailedQuery(pinQuery)
  useRetryFailedQuery(outputDecodersQuery)

  const stationReadiness = useMemo(
    () =>
      stationStepReadiness({
        plants: plantData?.list?.length ?? 0,
        soils: soilData?.list?.length ?? 0,
        emitters: emitterData?.list?.length ?? 0,
        areas: countAreas(areaData?.list as any),
        subAreas: countSubAreas(areaData?.list as any),
      }),
    [plantData, soilData, emitterData, areaData],
  )
  stationReadyRef.current = stationReadiness.ready

  const railSteps = [RESET_STEP, ...steps]
  const [progress, setProgress] = useState<GuidedSetupProgress | null>(() =>
    loadGuidedSetupProgress(interfaceId),
  )
  const [isResetting, setIsResetting] = useState(false)
  const [resetPhase, setResetPhase] = useState(0)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  // Focus mode: the step keeps the whole viewport and the wizard chrome steps
  // aside. Entering data into a long list is the one part of this wizard that
  // is worth more room than the navigation around it.
  const [focusMode, setFocusMode] = useState(false)

  // Tracked by key rather than index, so the active step survives any change to
  // the list's shape.
  // Every embedded grid registers its bulk-edit save here; leaving a step
  // flushes them so nothing typed is lost by navigating on.
  const savesRef = useGuidedSavesRef()
  // What the steps say is still unfinished. A ref cannot re-render the wizard,
  // so the provider tells us when it changes and this counter does the rest.
  const incompleteRef = useGuidedIncompleteRef()
  const [incompleteVersion, setIncompleteVersion] = useState(0)
  const bumpIncomplete = useCallback(
    () => setIncompleteVersion((version) => version + 1),
    [],
  )
  /**
   * SCRUM-1032 QC item 11 — the saving notice is dismissable, and comes back
   * for the next save rather than being silenced for the rest of the sitting.
   */
  const [savingNoticeDismissed, setSavingNoticeDismissed] = useState(false)
  /** True while any grid holds work not yet on the server; see the provider. */
  const unsavedRef = useRef(false)
  /**
   * SCRUM-1032 — the step's own background writes, reported in one place.
   *
   * Each grid used to raise a success toast of its own on every autosave, so
   * entering six plants raised six toasts and an error had to compete with
   * them. The grids stay quiet inside the wizard and this says it instead.
   */
  const [autosaveState, setAutosaveState] = useState<GuidedAutosaveState>('idle')

  const [activeKey, setActiveKey] = useState(() =>
    progress && steps.some((step) => step.key === progress.activeStepKey)
      ? progress.activeStepKey
      : steps[0].key,
  )
  // Re-read on every step change. The counts are what the gate is decided on,
  // and the step that produces them is the one the user just left — a cached
  // answer would keep saying "add a sub-area" to someone looking at the one
  // they added a moment ago.
  useEffect(() => {
    if (!Number.isFinite(interfaceId)) return
    void refetchPlants()
    void refetchSoils()
    void refetchEmitters()
    void refetchAreas()
    void refetchHydraulic()
    void refetchEtReferences()
    void refetchPins()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, interfaceId])

  const [visited, setVisited] = useState<Set<string>>(
    () =>
      new Set(
        progress?.visitedStepKeys.filter((key) =>
          steps.some((step) => step.key === key),
        ) ?? [steps[0].key],
      ),
  )
  const activeStep = Math.max(
    0,
    steps.findIndex((s) => s.key === activeKey),
  )
  const visitedIndexes = useMemo(
    () =>
      new Set(
        steps
          .map((s, index) => (visited.has(s.key) ? index + 1 : -1))
          .filter((index) => index >= 0),
      ),
    [steps, visited],
  )
  const isResetStep = !progress
  const activeRailStep = isResetStep ? 0 : activeStep + 1
  const railVisitedIndexes = isResetStep
    ? new Set([0])
    : new Set([0, ...visitedIndexes])
  // The step's own key — 'station'. Not to be confused with the guided-edit
  // registration key the station grid uses, which is 'stations'.
  const stationStepIndex = steps.findIndex((s) => s.key === 'station')
  const geotagStepIndex = steps.findIndex((s) => s.key === 'geotag')
  // Geotagging is pinning stations on a map, so it is held behind the same
  // gate as the step that creates them: there is nothing to pin until then.
  const disabledRailIndexes = isResetStep
    ? new Set(steps.map((_, index) => index + 1))
    : new Set(
        stationReadiness.ready || stationStepIndex < 0
          ? [0]
          : [0, stationStepIndex + 1, geotagStepIndex + 1].filter(
              (index) => index > 0,
            ),
      )

  /**
   * "a plant, a soil and an area" — the missing list, as a sentence. Both the
   * separator and the final conjunction come from keys; neither is English.
   */
  const describeMissing = (missing: StationPrerequisite[]) => {
    const worded = missing.map((item) => t(PREREQUISITE_KEYS[item]))
    if (worded.length === 0) return ''
    if (worded.length === 1) return worded[0]
    return t('guidedSetupListLast', {
      items: worded.slice(0, -1).join(t('guidedSetupListSeparator')),
      last: worded[worded.length - 1],
    })
  }

  /** The same joining, for names the server gave rather than worded items. */
  const listNames = (names: string[]) => {
    if (names.length === 0) return ''
    if (names.length === 1) return names[0]
    return t('guidedSetupListLast', {
      items: names.slice(0, -1).join(t('guidedSetupListSeparator')),
      last: names[names.length - 1],
    })
  }

  /** Named on the rail and on Next, so the block is never a dead control. */
  const stationBlockedReason = stationReadiness.ready
    ? undefined
    : t('guidedSetupStationBlocked', {
        items: describeMissing(stationReadiness.missing),
      })

  // The step before Station cannot advance into it either; the rail alone would
  // leave Next as the way around the rule.
  const nextEntersStation =
    stationStepIndex >= 0 && activeStep + 1 === stationStepIndex
  const isNextBlocked = nextEntersStation && !stationReadiness.ready

  // SCRUM-976 / SCRUM-1032: each step's inline-grid edits + deletes are scoped to the URL :id, but
  // add/edit done through the config slide-in drawers currently follows the globally-selected
  // interface (the shared sidebar views read AuthContext). Surface a mismatch so the user knows a
  // drawer edit would land on the selected controller, not the one being set up. Full scoping of the
  // drawers is tracked as a follow-up (SCRUM-1041).

  // Escape is the way out of any full-screen surface; without it the only exit
  // is a button the user has to find again in the toolbar.
  useEffect(() => {
    if (!focusMode) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Escape belongs to whatever the user opened last: a cell editor, a
      // select's list, a drawer. Only collapse the layout when nothing else
      // is claiming it.
      const target = event.target as HTMLElement | null
      if (
        target?.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '') ||
        // `:not(.MuiModal-hidden)` matters: the shared config drawer stays
        // mounted-but-hidden for the whole session.
        document.querySelector(
          '.MuiModal-root:not(.MuiModal-hidden), .MuiPopover-root:not(.MuiModal-hidden), [role="listbox"], [role="menu"]',
        )
      ) {
        return
      }
      setFocusMode(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [focusMode])

  const goToSetupOverview = () =>
    navigate(CONFIG_SETUP_ROUTES.setupFirst.to(id ?? ''))

  /**
   * Save first, move second — and stay put if the save fails on the way
   * forward, so a failed write is visible on the step that produced it. See
   * `useGuidedSaveThen` for which moves hold and why Exit is never dropped.
   */
  const { saveThen, isSaving, isSavingRef } = useGuidedSaveThen({
    flush: () => {
      setSavingNoticeDismissed(false)
      return runGuidedSaves(savesRef)
    },
    onHeld: (error) => {
      // A refusal is toasted by its grid; a save that never answered is not.
      if (error instanceof GuidedSaveTimeoutError) {
        toast.error(t('guidedSetupSaveTimedOutHeld'), {
          id: 'guided-save-timeout',
          duration: 8000,
        })
      }
    },
    onLeftWithFailure: (error) =>
      /**
       * SCRUM-1032 QC round 7 follow-up — leaving says what it left behind.
       *
       * The grid's toast names the refusal, but not that going Back or Exit
       * throws the refused row away: a soil refused by the server was simply
       * gone when the user came back to the step.
       */
      toast.error(
        error instanceof GuidedSaveIncompleteError
          ? t('guidedSetupLeftDiscarded', { count: error.failed })
          : error instanceof GuidedSaveTimeoutError
            ? t('guidedSetupSaveTimedOutLeft')
            : t('guidedSetupLeftDiscardedUnknown'),
        // Top centre: the wizard's Exit (×) is top right, where toasts
        // land, and this one is raised as the user is moving about.
        { id: 'guided-left-unsaved', duration: 8000, position: 'top-center' },
      ),
    onExitQueued: () => {
      setSavingNoticeDismissed(false)
      toast(t('guidedSetupExitAfterSave'), { id: 'guided-exit-queued' })
    },
  })

  // A reload, a closed tab or the browser's Back flush nothing: ask first.
  useWarnBeforeUnload(() => isSavingRef.current || unsavedRef.current)

  /**
   * What the current step says is unfinished, if anything.
   *
   * Only ever holds Finish, not Next: moving between steps to fill something
   * in is the wizard working as intended, while Finish is the end of it —
   * leaving there with rows still to enter is how a setup ends up half done.
   *
   * Declared above the early return below: a hook after one runs on some
   * renders and not others, which is how React loses track of hook order.
   */
  const incompleteReason = useMemo(() => {
    void incompleteVersion
    return Array.from(incompleteRef.current.values())[0] ?? null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incompleteVersion, activeKey])

  // A step change starts the status over: it belongs to the step that earned it.
  useEffect(() => setAutosaveState('idle'), [activeKey])

  /**
   * SCRUM-1032 QC round 3, item 5 — "All changes saved" is a passing notice.
   *
   * Standing in the corner of the step, it read as a claim about everything on
   * screen — including a draft row still amber because it has not been written
   * yet, which is exactly the pair Wei Woon photographed on 21 Sep. Said once
   * from the top right, where the rest of the app says the same kind of thing,
   * and gone a moment later. The fixed id means a run of autosaves replaces the
   * toast instead of stacking a column of them.
   *
   * Saving and failing stay on the step: one is a state to wait out, the other
   * is worth leaving on screen.
   */
  useEffect(() => {
    if (autosaveState !== 'saved') return
    toast.success(t('guidedSetupAutosaveSaved'), {
      id: 'guided-autosave-saved',
    })
  }, [autosaveState, t])

  /** True from the click on Finish until the server has answered. */
  const [isFinishing, setIsFinishing] = useState(false)
  /** Set once Finish succeeds, so the redirect above leaves its own exit alone. */
  const finishedRef = useRef(false)

  const { interfaceId: globalInterfaceId } = useAuthContext()

  /**
   * SCRUM-1032 QC item 16 — the wizard follows the selected controller.
   *
   * The app has one selected controller, shared across tabs through
   * localStorage and a `storage` listener. If it is not the controller this
   * wizard is for, the wizard is the stale thing: the user has moved on, and
   * every add/edit drawer on this page writes to the selected controller
   * rather than the one on screen, so staying is also unsafe.
   *
   * It used to warn about exactly that and carry on. Now it leaves, handing
   * back to the dashboard, which sends a controller still pending setup on to
   * its own setup — so switching to either kind lands somewhere sensible.
   *
   * Deliberately not limited to a switch made after this page opened. Arriving
   * with the mismatch already there — a second tab, a reload, a bookmarked
   * link — is the same situation and was the case that kept failing: the
   * warning fired and nothing else happened.
   *
   * Finish is excluded. It flips the status and navigates itself, and must not
   * be raced.
   */
  useEffect(() => {
    if (globalInterfaceId == null || Number.isNaN(interfaceId)) return
    if (Number(globalInterfaceId) === interfaceId) return
    if (isFinishing || finishedRef.current) return

    toast(t('guidedSetupInterfaceSwitched'))
    navigate(OPERATIONS_ROUTES.index.to, { replace: true })
    // `t` is left out on purpose: it changes identity when the language does,
    // and this is not worth raising again for that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalInterfaceId, interfaceId, isFinishing, navigate])

  /**
   * SCRUM-1032 QC item 16 — the wizard is not a place a set-up controller can
   * be left sitting.
   *
   * Reached by a stale link, the browser's Back button, or a picker that used
   * to send every controller here regardless of status. There is nothing to do
   * on a wizard for a controller that has finished it, and no way out of one,
   * so it hands back to the dashboard instead.
   *
   * Held off while Finish is running and once it has succeeded: that call is
   * what flips the status, and this must not race the navigation it already
   * does — otherwise finishing a setup would look like being thrown out of it.
   */
  useEffect(() => {
    if (selectedInterface?.status !== true) return
    if (isFinishing || finishedRef.current) return
    // Redux carries the status the dashboard's own guard reads. Without this
    // it still holds the stale `false`, sees a controller mid-setup, and sends
    // the user back here — which this effect answers by sending them out
    // again, forever.
    if (!Number.isNaN(interfaceId)) {
      dispatch(updateInterfaceStatus({ id: interfaceId, status: true }))
    }
    navigate(OPERATIONS_ROUTES.index.to, { replace: true })
  }, [selectedInterface?.status, isFinishing, navigate, dispatch, interfaceId])

  const [showFinishConfirm, setShowFinishConfirm] = useState(false)

  /**
   * Stations with nowhere on the map, for the dialog to mention.
   *
   * Geotagging is the one thing in the wizard that is optional, which makes it
   * the one thing a user can leave without noticing. Fetched only once Finish
   * is in reach — on a controller with a thousand stations this is not a list
   * to pull on the way past.
   */
  const { data: stationLocationData } = useSearchStation(
    { PageSize: 0, PageIndex: 1 },
    interfaceId,
    { enabled: showFinishConfirm } as any,
  )
  // SCRUM-1032 QC item 7 — the server's own count; see unpinnedStationCount.
  const unpinnedCount = useMemo(
    () => unpinnedStationCount(stationLocationData as any),
    [stationLocationData],
  )

  /**
   * What the Station step last said was unfinished.
   *
   * A step's report is cleared when its grid unmounts, and Station is no
   * longer the last step — so without this, walking on to the geotag step
   * turned "109 stations still to fill in" into a clean Finish. Remembered
   * while the user is on Station, and cleared there the moment it is true.
   */
  const stationIncompleteRef = useRef<string | null>(null)
  useEffect(() => {
    if (activeKey === 'station') stationIncompleteRef.current = incompleteReason
  }, [activeKey, incompleteReason])

  if (!id || Number.isNaN(interfaceId)) {
    return (
      <SetUpLayout title={t('guidedSetupDocumentTitle')}>
        <Stack
          spacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
            p: 4,
          }}
        >
          <Typography
            sx={{ color: 'text.main', fontSize: '16px', fontWeight: 600 }}
          >
            {t('guidedSetupMissingControllerTitle')}
          </Typography>
          <Typography sx={{ color: 'grey.600', fontSize: '13px' }}>
            {t('guidedSetupMissingControllerHint')}
          </Typography>
          <Button variant="secondary" onClick={goToSetupOverview}>
            {t('guidedSetupBackToSetup')}
          </Button>
        </Stack>
      </SetUpLayout>
    )
  }

  const handleReset = async () => {
    setIsResetting(true)
    setResetPhase(0)
    try {
      let response = await comonDataApi.deleteCommonType(interfaceId, 1)
      if (!response.data.success) throw new Error(resetFailure(t, 0, response.data))

      setResetPhase(1)
      // The paged pass answers 200 with `success: false` when the page has
      // nothing behind it, so a controller whose stations are already gone
      // failed its own reset — "Http request failed" — with nothing actually
      // wrong. A first page that does not succeed is that empty case and ends
      // the paging; one that fails after a page has been deleted is a real
      // failure, since data was going and stopped half way.
      let page = 1
      do {
        response = await comonDataApi.deleteCommonType(interfaceId, 2, page)
        if (!response.data.success) {
          if (page > 1) throw new Error(resetFailure(t, 1, response.data))
          break
        }
        page += 1
      } while (response.data.isPaging === true && page <= RESET_MAX_PAGES)

      setResetPhase(2)
      response = await comonDataApi.deleteCommonType(interfaceId, 3)
      if (!response.data.success) throw new Error(resetFailure(t, 2, response.data))

      setResetPhase(RESET_PHASE_KEYS.length)

      // The same wipe the admin list's reset does: edited-row markers and
      // staged station rows describe data the server has just dropped, and a
      // fresh setup must not start with them. Before the new progress is
      // written, which this would otherwise take with it.
      clearGuidedSetupStorage(interfaceId)

      const nextProgress = beginGuidedSetupProgress(interfaceId, steps[0].key)
      setProgress(nextProgress)
      setActiveKey(steps[0].key)
      setVisited(new Set([steps[0].key]))
      queryClient.invalidateQueries([queryKeys.setupCommonData, interfaceId])
      // The whole-list decoder query is held for the sitting (`staleTime:
      // Infinity`) and is keyed off nothing the reset touches, so without this
      // the decoder step opened on the list as it was before the wipe —
      // purposes and all — from cache rather than from the server.
      queryClient.removeQueries([queryKeys.guidedAllDecoders, interfaceId])
      queryClient.invalidateQueries([queryKeys.controller])
      toast.success(t('guidedSetupResetSuccess'))
    } catch (error: any) {
      toast.error(error?.message || t('guidedSetupResetError'))
    } finally {
      setIsResetting(false)
      setShowResetConfirm(false)
    }
  }

  const isFirst = activeStep === 0
  const isLast = activeStep === steps.length - 1
  const step = steps[activeStep]
  const StepComponent = step.Component

  /**
   * QC round 2, items 2 / 7 / 8 — how far the user may go.
   *
   * Each must-fill step is judged on what the server holds, except where the
   * open grid knows better: the decoder grid counts purposes typed but not yet
   * written, and only the station grid knows which rows are drafts. The station
   * answer is remembered after the user leaves (see `stationIncompleteRef`);
   * the decoder one is not needed then, because leaving the step writes it.
   */
  const stepData = {
    outputDecodersWithoutPurpose: outputDecoders
      ? countWithoutPurpose(outputDecoders)
      : undefined,
    mainlines: hydraulicData ? countMainlines(hydraulicData.list) : undefined,
    etReferences: etReferenceData
      ? (Array.isArray(etReferenceData.list) ? etReferenceData.list.length : 0)
      : undefined,
    plants: plantData ? (plantData.list?.length ?? 0) : undefined,
    soils: soilData ? (soilData.list?.length ?? 0) : undefined,
    emitters: emitterData ? (emitterData.list?.length ?? 0) : undefined,
    areas: areaData ? countAreas(areaData.list as any) : undefined,
    subAreas: areaData ? countSubAreas(areaData.list as any) : undefined,
    areasWithoutSubArea: areaData
      ? areasWithoutSubArea(areaData.list as any)
      : undefined,
    inputDecodersWithoutName: inputDecoders
      ? countWithoutName(inputDecoders)
      : undefined,
    sdi12DecodersWithoutName: sdi12Decoders
      ? countWithoutName(sdi12Decoders)
      : undefined,
    unpinnedStations:
      typeof (pinData as any)?.unpinnedStations === 'number'
        ? (pinData as any).unpinnedStations
        : undefined,
    stationDecoders: outputDecoders
      ? outputDecoders.filter(isStationDecoder).length
      : undefined,
    stations:
      typeof (pinData as any)?.totalCount === 'number'
        ? (pinData as any).totalCount
        : undefined,
  }
  const reportedFor = (key: string): string | null | undefined => {
    if (key === 'station') {
      if (key === activeKey) return incompleteReason
      // A remembered "still to fill in" stands. A remembered "done" does not:
      // it may predate purposes set since, or never have been said at all this
      // session — then the server counts decide (QC round 7 review).
      return stationIncompleteRef.current ?? undefined
    }
    // Before its list has loaded the grid has nothing to count, and its
    // silence would read as "complete".
    if (key === 'decodersOutput' && key === activeKey && outputDecoders) {
      return incompleteReason
    }
    return undefined
  }
  const gapOf = (key: string) => stepGap(key, stepData, reportedFor(key))
  const blockingIndex = isResetStep
    ? -1
    : firstBlockingStep(
        steps.map((s) => s.key),
        gapOf,
      )
  const blockingStep = blockingIndex >= 0 ? steps[blockingIndex] : undefined
  const blockingGap = blockingStep ? gapOf(blockingStep.key) : null
  // The decoder count follows the grid's own refetches; the station total is
  // re-read on step change.
  polledCountsRef.current = new Set(
    (['plants', 'soils', 'emitters', 'areas', 'mainlines'] as const).filter(
      (count) =>
        shouldPollCount(count, {
          activeKey: isResetStep ? null : activeKey,
          value: stepData[count],
          stationReady: stationReadiness.ready,
          blockingKey: blockingStep?.key,
        }),
    ),
  )

  const describeGap = (gap: StepGap) => {
    switch (gap.kind) {
      case 'decoderPurpose':
        return t(
          gap.count === 1
            ? 'guidedSetupGapDecoderPurposeOne'
            : 'guidedSetupGapDecoderPurposeOther',
          { count: gap.count },
        )
      case 'mainline':
        return t('guidedSetupGapMainline')
      case 'stationsMissing':
        return t(
          gap.count === 1 ? 'stationsBlockedPendingOne' : 'stationsBlockedPendingMany',
          { count: gap.count },
        )
      case 'prerequisites':
        return t('guidedSetupGapAdd', { items: describeMissing(gap.missing) })
      case 'areaWithoutSubArea':
        return t(
          gap.names.length === 1
            ? 'guidedSetupGapAreaSubAreaOne'
            : 'guidedSetupGapAreaSubAreaOther',
          { areas: listNames(gap.names) },
        )
      case 'reported':
        return gap.reason
      case 'checking':
        return t('guidedSetupGapChecking')
    }
  }
  /** Says which step is holding the user when it is not the one on screen. */
  const gapMessage =
    blockingStep && blockingGap
      ? blockingIndex === activeStep
        ? t('guidedSetupNextBlockedHere', { reason: describeGap(blockingGap) })
        : t('guidedSetupNextBlockedEarlier', {
            step: t(blockingStep.labelKey),
            reason: describeGap(blockingGap),
          })
      : undefined
  const nextHeldByGap =
    !isLast && !canMoveTo(activeStep, activeStep + 1, blockingIndex)
  const isNextHeld = isNextBlocked || nextHeldByGap
  const nextHeldReason = nextHeldByGap ? gapMessage : stationBlockedReason
  // The rail cannot jump past the step that is holding the user either.
  const railHeldIndexes = steps
    .map((_, index) => index)
    .filter((index) => !canMoveTo(activeStep, index, blockingIndex))
  const railDisabledIndexes = new Set([
    ...Array.from(disabledRailIndexes),
    ...railHeldIndexes.map((index) => index + 1),
  ])
  // QC round 2, item 4 — tick for a step whose data is in, exclamation mark for
  // one visited and left unfinished, number for the rest.
  const railStatuses: RailStepStatus[] = [
    isResetStep ? 'current' : 'done',
    ...steps.map((s, index) =>
      railStepStatus({
        optional: !MUST_FILL_STEP_KEYS.has(s.key),
        isActive: !isResetStep && index === activeStep,
        visited: !isResetStep && visited.has(s.key),
        complete:
          stepComplete(s.key, stepData, reportedFor(s.key)) &&
          // Station and geotag cannot be opened without their prerequisites,
          // and "nothing to enter" there is not the same as done.
          (!['station', 'geotag'].includes(s.key) || stationReadiness.ready),
      }),
    ),
  ]
  const incompleteHint = (key: string): string | undefined => {
    const gap = gapOf(key)
    if (gap) return describeGap(gap)
    if ((key === 'station' || key === 'geotag') && stationBlockedReason) {
      return stationBlockedReason
    }
    if (key === 'etReference') return t('guidedSetupGapEtReference')
    if (key === 'decodersInput' || key === 'decodersSdi12') {
      const count =
        (key === 'decodersInput'
          ? stepData.inputDecodersWithoutName
          : stepData.sdi12DecodersWithoutName) ?? 0
      return t(
        count === 1 ? 'guidedSetupGapDecoderNameOne' : 'guidedSetupGapDecoderNameOther',
        { count },
      )
    }
    if (key === 'geotag') {
      const count = stepData.unpinnedStations ?? 0
      return t(
        count === 1
          ? 'guidedSetupUnpinnedStationOne'
          : 'guidedSetupUnpinnedStationOther',
        { count },
      )
    }
    return undefined
  }
  const railIncompleteReasons: Record<number, string> = {}
  railStatuses.forEach((status, railIndex) => {
    if ((status !== 'incomplete' && status !== 'optional') || railIndex === 0) return
    const hint = incompleteHint(steps[railIndex - 1].key)
    if (hint) railIncompleteReasons[railIndex] = hint
  })

  const railDisabledReasons: Record<number, string> = {}
  if (stationStepIndex >= 0 && stationBlockedReason) {
    railDisabledReasons[stationStepIndex + 1] = stationBlockedReason
  }
  if (gapMessage) {
    railHeldIndexes.forEach((index) => {
      railDisabledReasons[index + 1] = gapMessage
    })
  }

  const goTo = (index: number) => {
    if (!progress) return
    const next = steps[index]
    if (!next) return
    const nextVisited = new Set(visited).add(next.key)
    // Expanded view is kept across steps that have it (QC round 2, item 6),
    // and dropped on one that does not: there it has no toggle, and Escape
    // would be the only way back.
    if (!FOCUS_STEP_KEYS.has(next.key)) setFocusMode(false)
    setActiveKey(next.key)
    setVisited(nextVisited)
    setProgress(updateGuidedSetupProgress(progress, next.key, nextVisited))
  }

  /**
   * SCRUM-1032 QC round 3, item 7 — every step flushes on the way out, the
   * station step included.
   *
   * It used to be exempt: its registered save was the full bulk save, which
   * refuses a batch holding any incomplete row and names the missing fields —
   * a complaint about work the user has not finished yet, raised because they
   * clicked another step. So the flush was skipped, and Sub Area edited on
   * three rows followed by a click on the rail wrote nothing unless the pause
   * autosave happened to be mid-flight (Wei Woon, 21 Sep).
   *
   * The step now registers a quiet flush instead (`flushReadyRows`), which
   * writes what is ready and leaves the rest staged, so there is nothing left
   * to skip.
   */

  const exitSetup = () => saveThen(goToSetupOverview, { isExit: true })

  /**
   * The unit is read when a value is written, so rows typed in the old unit
   * are written first; anything refused or still writing holds the change.
   */
  const flushBeforeUnitChange = () =>
    isSavingRef.current
      ? Promise.resolve(false)
      : saveThen(() => undefined, { holdOnFailure: true })

  const onBack = () =>
    isFirst
      ? exitSetup()
      : saveThen(() => goTo(activeStep - 1))

  /**
   * The end of the wizard: tell the server the controller is set up, then hand
   * back to the app.
   *
   * The status is the server's to change — until `complete` is called the
   * controller is Pending Setup, and the app keeps sending the user back here.
   * So the navigation waits for it: leaving on a failed call would drop the
   * user on a dashboard that bounces them straight back with nothing said.
   */
  const onFinish = async () => {
    if (!progress || isFinishing) return
    setIsFinishing(true)
    try {
      const { data } = await setupApi.completeSetup(interfaceId)
      if (data && data.success === false) {
        throw new Error(data.message || t('guidedSetupCompleteError'))
      }

      // Redux holds the status the route guards read; without this the
      // dashboard sees a controller still in setup and turns the user round.
      finishedRef.current = true
      dispatch(updateInterfaceStatus({ id: interfaceId, status: true }))
      queryClient.invalidateQueries([queryKeys.setupCommonData, interfaceId])
      queryClient.invalidateQueries([queryKeys.interface])
      setProgress(completeGuidedSetupProgress(progress, activeKey, visited))
      setShowFinishConfirm(false)
      navigate(OPERATIONS_ROUTES.index.to)
    } catch (error: any) {
      toast.error(getApiErrorMessage(error))
    } finally {
      setIsFinishing(false)
    }
  }

  // Finish needs every must-fill step done, as well as whatever the last
  // step's own grid still reports.
  const finishReason =
    gapMessage ??
    (incompleteReason
      ? t('guidedSetupFinishBlocked', { reason: incompleteReason })
      : undefined)
  const isFinishBlocked = isLast && !!finishReason

  const onNext = () => {
    if (isNextHeld || isFinishBlocked || isFinishing) return
    // The pending edits go in either way; Finish then asks before it changes
    // what the controller is.
    saveThen(
      () => (isLast ? setShowFinishConfirm(true) : goTo(activeStep + 1)),
      // Forward is the direction that loses work when a save was refused.
      { holdOnFailure: true },
    )
  }

  const header = (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        px: '32px',
        py: '18px',
        borderBottom: '1px solid',
        borderColor: 'border.main',
      }}
    >
      <Stack spacing={0.25}>
        <Typography
          sx={{ color: 'text.main', fontSize: '20px', fontWeight: 700 }}
        >
          {t('guidedSetupTitle')}
        </Typography>
        {/* grey.700, not 600: at 13px the 600 (#7C7B7B) lands at 4.22:1 on the
            light ground, just under what small text needs. */}
        <Typography sx={{ color: 'grey.700', fontSize: '13px' }}>
          {selectedInterface?.customName
            ? `${selectedInterface.customName} (${selectedInterface.name ?? ''})`
            : t('guidedSetupSubtitle')}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {/* The wizard has none of the app chrome, so this is the only way to
            reach language / units / theme while filling it in. */}
        <GuidedSettingsMenu beforeUnitChange={flushBeforeUnitChange} />
        {/* Leaving the wizard is a close, not a route choice: the Excel import
            is reachable from Getting Started. */}
        <IconButton
          aria-label={t('guidedSetupCloseAria')}
          onClick={exitSetup}
          sx={{
            width: 34,
            height: 34,
            border: '1px solid',
            borderColor: 'border.main',
            color: 'text.main',
            '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
    </Stack>
  )

  const footer = isResetStep ? (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        px: '32px',
        py: '16px',
        borderTop: '1px solid',
        borderColor: 'border.main',
      }}
    >
      <Typography
        sx={{ color: 'text.main', fontSize: '13px', fontWeight: 500 }}
      >
        {t('guidedSetupStepCounter', { current: 1, total: railSteps.length })}
        <Box component="span" sx={{ color: 'grey.800', fontWeight: 400 }}>
          {' — '}
          {t(RESET_STEP.labelKey)}
        </Box>
      </Typography>
      <Stack direction="row" spacing={1.5}>
        <Button
          variant="secondary"
          startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
          sx={backButtonSx}
          onClick={goToSetupOverview}
          disabled={isResetting}
        >
          {t('commonActionsBack')}
        </Button>
        <Button
          variant="primary"
          endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
          sx={disabledNextButtonSx}
          disabled
        >
          {t('commonActionsNext')}
        </Button>
      </Stack>
    </Stack>
  ) : (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        px: '32px',
        py: '16px',
        borderTop: '1px solid',
        borderColor: 'border.main',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
        <Typography
          sx={{ color: 'text.main', fontSize: '13px', fontWeight: 500 }}
        >
          {t('guidedSetupStepCounter', {
            current: activeRailStep + 1,
            total: railSteps.length,
          })}
          <Box component="span" sx={{ color: 'grey.800', fontWeight: 400 }}>
            {' — '}
            {t(step.labelKey)}
          </Box>
        </Typography>
        {/* SCRUM-1032 — the step's background writes, said once. In the
            footer beside the step name: floating over the step it covered
            the grid's scrollbar and the Common Emitters chips (round 7
            follow-up). Hidden while the flush notice is up, which says the
            same thing more loudly. */}
        {(autosaveState === 'saving' || autosaveState === 'error') &&
          !isSaving && (
            <Stack
              direction="row"
              alignItems="center"
              spacing="7px"
              data-testid="guided-autosave-status"
              role="status"
            >
              {autosaveState === 'saving' && <CircularProgress size={13} />}
              <Typography
                sx={
                  autosaveState === 'error'
                    ? BLOCKED_REASON_SX
                    : { fontSize: '12px', color: 'grey.800' }
                }
              >
                {autosaveState === 'saving'
                  ? t('guidedSetupAutosaveSaving')
                  : t('guidedSetupAutosaveError')}
              </Typography>
            </Stack>
          )}
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {/* A disabled Next with no reason beside it is a dead end. The dark
            tone matches the reset dialog's: #dc3545 only reaches 3.71:1 on the
            dark ground, and this line is 12px. */}
        {isNextHeld && !isLast && (
          <Typography
            sx={{ ...BLOCKED_REASON_SX, maxWidth: 420 }}
            data-testid="guided-next-blocked"
          >
            {nextHeldReason}
          </Typography>
        )}
        {isFinishBlocked && (
          <Typography
            sx={{ ...BLOCKED_REASON_SX, maxWidth: 420 }}
            data-testid="guided-next-blocked"
          >
            {finishReason}
          </Typography>
        )}
        <Button
          variant="secondary"
          startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
          sx={backButtonSx}
          onClick={onBack}
          disabled={isSaving}
        >
          {t('commonActionsBack')}
        </Button>
        <Button
          variant="primary"
          endIcon={
            isSaving || isFinishing ? (
              <CircularProgress size={14} sx={{ color: '#fff' }} />
            ) : (
              <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={nextButtonSx}
          onClick={onNext}
          disabled={isSaving || isFinishing || isNextHeld || isFinishBlocked}
        >
          {isFinishing
            ? t('guidedSetupFinishing')
            : isSaving
              ? t('guidedSetupSaving')
              : isLast
                ? t('guidedSetupFinish')
                : t('commonActionsNext')}
        </Button>
      </Stack>
    </Stack>
  )

  return (
    // SCRUM-1032: the embedded config grids (Hydraulic, ET Reference, Plant/Soil/Emitter, Areas,
    // Station) call useConfigUI() and drive add/edit drawers + delete modals through it. On /config
    // that context + chrome come from ConfigContainer; the guided shell supplies the same here so the
    // steps render and their drawers/modals work — reusing ConfigContainer's SidebarUI/ModalUI.
    <ConfigUIProvider>
      <GuidedEditProvider
        bulkEditByDefault
        savesRef={savesRef}
        incompleteRef={incompleteRef}
        onIncompleteChange={bumpIncomplete}
        onAutosaveStateChange={setAutosaveState}
        unsavedRef={unsavedRef}
      >
        <SetUpLayout
          title={t('guidedSetupDocumentTitle')}
          header={focusMode ? undefined : header}
          // The footer stays in expanded view: Back and Next are how the user
          // moves on without leaving it (QC round 2, item 6).
          footer={footer}
          fullWidth
          constrainViewport
        >
          <Stack direction="row" sx={{ flexGrow: 1, minHeight: 0 }}>
            {/* Left rail — the dataset steps */}
            {!focusMode && (
              <Box
                sx={{
                  width: 224,
                  flexShrink: 0,
                  borderRight: '1px solid',
                  borderColor: 'border.main',
                  p: '24px 16px',
                  overflowY: 'auto',
                }}
              >
                <GuidedStepRail
                  steps={railSteps}
                  activeStep={activeRailStep}
                  visited={railVisitedIndexes}
                  disabled={railDisabledIndexes}
                  disabledReason={railDisabledReasons}
                  statuses={railStatuses}
                  incompleteReason={railIncompleteReasons}
                  onSelect={(index) =>
                    index > 0 &&
                    !isResetStep &&
                    !railDisabledIndexes.has(index) &&
                    saveThen(() => goTo(index - 1), {
                      // Forward, like Next: a refused save keeps the user
                      // here. So does the step already open: nothing is
                      // left, so "Left without saving" would be untrue.
                      holdOnFailure: index - 1 >= activeStep,
                    })
                  }
                />
              </Box>
            )}

            {/* Active step content */}
            <Box
              sx={{
                position: 'relative',
                flexGrow: 1,
                minWidth: 0,
                overflow: 'auto',
              }}
            >
              {isResetStep ? (
                <Stack
                  spacing={2}
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100%',
                    maxWidth: 640,
                    mx: 'auto',
                    p: 4,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: 'text.main',
                      fontSize: '24px',
                      fontWeight: 700,
                    }}
                  >
                    {t('guidedSetupResetCommonData')}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'grey.600',
                      fontSize: '15px',
                      lineHeight: 1.5,
                    }}
                  >
                    {t('guidedSetupResetIntro')}
                  </Typography>
                  <Button
                    variant="primary"
                    endIcon={
                      isResetting ? (
                        <CircularProgress size={14} sx={{ color: '#fff' }} />
                      ) : (
                        <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    sx={nextButtonSx}
                    onClick={() => setShowResetConfirm(true)}
                    disabled={isResetting}
                  >
                    {isResetting
                      ? t('guidedSetupResetting')
                      : t('guidedSetupResetCommonData')}
                  </Button>
                </Stack>
              ) : (
                <StepComponent
                  interfaceId={interfaceId}
                  focusMode={focusMode}
                  onToggleFocus={() => setFocusMode((value) => !value)}
                />
              )}

              {/* SCRUM-1032 QC item 11 — a notice, not a shutter.
                  This used to be a full-bleed blur over the step, on the
                  reasoning that the user must not keep typing while the rows
                  are written. On a control hub that write is 500-odd requests,
                  and Wei Woon sat behind the blur for 45 minutes with nothing
                  to do and no way to look at the list underneath.
                  The grid's own toolbar now reports "Saving 81 of 511" with a
                  Stop beside it, so the progress no longer needs a shutter to
                  live on. What is left worth saying — do not close the window —
                  is said here, out of the way and dismissable. Next and Back
                  stay disabled while it runs, which is what actually has to be
                  prevented. */}
              {isSaving && !savingNoticeDismissed && (
                <Stack
                  direction="row"
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bottom: 16,
                    zIndex: 5,
                    maxWidth: 'calc(100% - 32px)',
                    alignItems: 'center',
                    gap: '12px',
                    px: '14px',
                    py: '10px',
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: 'border.main',
                    backgroundColor: 'background.default',
                    boxShadow: '0 8px 24px rgba(16, 24, 40, 0.18)',
                  }}
                >
                  <CircularProgress size={18} />
                  <Stack sx={{ gap: '1px', minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: 'text.main',
                        fontSize: '13.5px',
                        fontWeight: 600,
                      }}
                    >
                      {t('guidedSetupSavingChanges')}
                    </Typography>
                    <Typography sx={{ color: 'grey.800', fontSize: '12px' }}>
                      {t('guidedSetupSavingNotice')}
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    aria-label={t('commonActionsClose')}
                    onClick={() => setSavingNoticeDismissed(true)}
                    sx={{ color: 'grey.800', ml: '4px' }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              )}
            </Box>
          </Stack>
        </SetUpLayout>
        {/* Finish is the end of the setup and the start of the controller
            running: it is worth one question first, and the one thing the user
            can still lose by leaving — a station with no place on the map — is
            worth saying out loud while there is something they can do about
            it. */}
        <Dialog
          open={showFinishConfirm}
          onClose={() => !isFinishing && setShowFinishConfirm(false)}
          aria-labelledby="finish-setup-title"
          aria-describedby="finish-setup-description"
          fullWidth
          maxWidth="sm"
        >
          <Box sx={{ backgroundColor: 'background.default' }}>
            <Stack sx={{ px: '30px', py: '18px' }}>
              <Typography
                id="finish-setup-title"
                sx={{
                  color: 'text.main',
                  fontSize: '22px',
                  fontWeight: 600,
                  lineHeight: 1.25,
                }}
              >
                {t('guidedSetupFinishTitle')}
              </Typography>
            </Stack>
            <Stack sx={{ px: '30px', pb: '30px', gap: '18px' }}>
              <Typography
                id="finish-setup-description"
                sx={{ color: 'text.main', fontSize: '18px', lineHeight: 1.45 }}
              >
                <Box
                  component="strong"
                  sx={{ fontSize: 'inherit', fontWeight: 700 }}
                >
                  {selectedInterface?.customName ||
                    selectedInterface?.name ||
                    t('guidedSetupThisControllerCapitalised')}
                  {selectedInterface?.customName && selectedInterface?.name
                    ? ` (${selectedInterface.name})`
                    : ''}
                </Box>{' '}
                {t('guidedSetupFinishDescription')}
              </Typography>

              {unpinnedCount > 0 && (
                <Typography
                  sx={{ color: 'grey.800', fontSize: '15px', lineHeight: 1.45 }}
                >
                  {unpinnedCount === 1
                    ? t('guidedSetupUnpinnedStationOne')
                    : t('guidedSetupUnpinnedStationOther', {
                        count: unpinnedCount,
                      })}{' '}
                  {t('guidedSetupUnpinnedStationHint')}
                </Typography>
              )}

              <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                <Button
                  variant="secondary"
                  disabled={isFinishing}
                  onClick={() => setShowFinishConfirm(false)}
                >
                  {t('guidedSetupBackToSetup')}
                </Button>
                <Button
                  variant="primary"
                  disabled={isFinishing}
                  endIcon={
                    isFinishing ? (
                      <CircularProgress size={14} sx={{ color: '#fff' }} />
                    ) : undefined
                  }
                  onClick={onFinish}
                >
                  {isFinishing
                    ? t('guidedSetupFinishing')
                    : t('guidedSetupFinishTitle')}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Dialog>

        <Dialog
          open={showResetConfirm}
          onClose={() => !isResetting && setShowResetConfirm(false)}
          aria-labelledby="reset-common-data-warning-title"
          aria-describedby="reset-common-data-warning-description"
          fullWidth
          maxWidth="sm"
        >
          <Box sx={{ backgroundColor: 'background.default' }}>
            <Stack sx={{ px: '30px', py: '18px' }}>
              <Typography
                id="reset-common-data-warning-title"
                sx={{
                  color: 'text.main',
                  fontSize: '22px',
                  fontWeight: 600,
                  lineHeight: 1.25,
                }}
              >
                {isResetting
                  ? t('guidedSetupResettingController')
                  : t('SetupWarning')}
              </Typography>
            </Stack>
            {isResetting ? (
              <ResetProgressPanel phase={resetPhase} />
            ) : (
            <Stack sx={{ px: '30px', pb: '30px', gap: '18px' }}>
              <Typography
                id="reset-common-data-warning-description"
                sx={{ color: 'text.main', fontSize: '18px', lineHeight: 1.45 }}
              >
                {t('guidedSetupResetWarningPrefix')}{' '}
                <Box
                  component="strong"
                  sx={{ fontSize: 'inherit', fontWeight: 700 }}
                >
                  {selectedInterface?.customName ||
                    selectedInterface?.name ||
                    t('guidedSetupThisController')}
                  {selectedInterface?.customName && selectedInterface?.name
                    ? ` (${selectedInterface.name})`
                    : ''}
                </Box>
                .
              </Typography>
              <Typography
                sx={{
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? '#FDA29B' : '#B42318',
                  fontSize: '16px',
                  fontWeight: 600,
                  lineHeight: 1.45,
                }}
              >
                {t('guidedSetupResetIrreversible')}
              </Typography>
              <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                <Button
                  variant="secondary"
                  onClick={() => setShowResetConfirm(false)}
                >
                  {t('commonActionsClose')}
                </Button>
                <Button variant="primary" onClick={handleReset}>
                  {t('commonActionsConfirm')}
                </Button>
              </Stack>
            </Stack>
            )}
          </Box>
        </Dialog>
      </GuidedEditProvider>
      {/* Shared config drawer + delete-modal chrome for every step (reused from ConfigContainer). */}
      <SidebarUI />
      <ModalUI />
    </ConfigUIProvider>
  )
}

/**
 * Keyed by the controller, so moving from one controller's wizard to
 * another's while mounted starts over: progress, open step and visited steps
 * are read once, when the wizard mounts.
 */
const SetUpGuided = memo(() => {
  const { id } = useParams()
  return <SetUpGuidedWizard key={id ?? ''} />
})

SetUpGuided.displayName = 'SetUpGuided'

export default SetUpGuided
